/**
 * comments-new.ts
 * 
 * Servizio per la gestione dei commenti tramite API.
 * Allineato con le API del backend UfficioStile.
 */

import { apiClient, buildQueryParams } from './base';
import {
  Comment,
  CommentCreateRequest,
  CommentUpdateRequest,
  CommentVisibility,
  ApiResponse,
  PagedResponse,
  SearchParams
} from '../../types/api';

/** Tipo per un commento cross-portal (da MergedCommentDto backend) */
export interface GlobalComment {
  id: string;
  content: string;
  createdByName: string;
  createdAt: string;
  isGlobal: boolean;
  sourcePortal?: string;
  commentOrigin: string;
  badge: string;
  canEdit: boolean;
  canDelete: boolean;
  commentType: string;
  parentCommentId?: string;
  threadLevel: number;
}

/** Risposta combinata di GET /Comment/by-request/{id} */
export interface CommentsWithGlobalResponse {
  comments: Comment[];
  globalComments: GlobalComment[];
}

export class CommentService {
  private baseUrl = '/Comment';

  // ================== OPERAZIONI CRUD BASE ==================
  async getAllComments(params?: SearchParams): Promise<PagedResponse<Comment>> {
    const queryString = params ? buildQueryParams(params) : '';
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    const response = await apiClient.get<PagedResponse<Comment>>(url);
    return response.data;
  }

  async getCommentById(id: string): Promise<Comment> {
    const response = await apiClient.get<Comment>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  async createComment(data: CommentCreateRequest): Promise<Comment> {
    const response = await apiClient.post<Comment>(this.baseUrl, data);
    return response.data;
  }

  async updateComment(id: string, data: CommentUpdateRequest): Promise<Comment> {
    const response = await apiClient.put<Comment>(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  async deleteComment(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  // ================== OPERAZIONI FILTRI ==================
  async getCommentsByRequest(requestId: string): Promise<CommentsWithGlobalResponse> {
    const response = await apiClient.get<CommentsWithGlobalResponse>(`${this.baseUrl}/by-request/${requestId}`);
    return response.data;
  }

  async getCommentsByTask(taskId: string): Promise<Comment[]> {
    const response = await apiClient.get<Comment[]>(`${this.baseUrl}/by-task/${taskId}`);
    return response.data;
  }

  async getCommentsByUser(userId: string): Promise<Comment[]> {
    const response = await apiClient.get<Comment[]>(`${this.baseUrl}/by-user/${userId}`);
    return response.data;
  }

  async getMyComments(): Promise<Comment[]> {
    const response = await apiClient.get<Comment[]>(`${this.baseUrl}/my-comments`);
    return response.data;
  }

  async getRecentCommentsByUser(userId: string): Promise<Comment[]> {
    const response = await apiClient.get<Comment[]>(`${this.baseUrl}/recent-by-user/${userId}`);
    return response.data;
  }

  // ================== OPERAZIONI VISIBILITÀ ==================
  async getPublicComments(params?: SearchParams): Promise<PagedResponse<Comment>> {
    const queryString = params ? buildQueryParams(params) : '';
    const url = queryString ? `${this.baseUrl}/public?${queryString}` : `${this.baseUrl}/public`;
    const response = await apiClient.get<PagedResponse<Comment>>(url);
    return response.data;
  }

  async getInternalComments(params?: SearchParams): Promise<PagedResponse<Comment>> {
    const queryString = params ? buildQueryParams(params) : '';
    const url = queryString ? `${this.baseUrl}/internal?${queryString}` : `${this.baseUrl}/internal`;
    const response = await apiClient.get<PagedResponse<Comment>>(url);
    return response.data;
  }

  async toggleCommentVisibility(id: string): Promise<void> {
    await apiClient.post(`${this.baseUrl}/${id}/toggle-visibility`);
  }

  // ================== OPERAZIONI RICERCA ==================
  async searchComments(params: {
    searchTerm?: string;
    visibility?: CommentVisibility;
    userId?: string;
    requestId?: string;
    taskId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PagedResponse<Comment>> {
    const queryString = buildQueryParams(params);
    const response = await apiClient.get<PagedResponse<Comment>>(`${this.baseUrl}/search?${queryString}`);
    return response.data;
  }

  async getCommentsByDateRange(startDate: string, endDate: string): Promise<Comment[]> {
    const params = { startDate, endDate };
    const queryString = buildQueryParams(params);
    const response = await apiClient.get<Comment[]>(`${this.baseUrl}/by-date-range?${queryString}`);
    return response.data;
  }

  async getRecentComments(limit: number = 10): Promise<Comment[]> {
    const params = { limit };
    const queryString = buildQueryParams(params);
    const response = await apiClient.get<Comment[]>(`${this.baseUrl}/recent?${queryString}`);
    return response.data;
  }

  // ================== STATISTICHE ==================
  async getCommentStatistics(): Promise<any> {
    const response = await apiClient.get<any>(`${this.baseUrl}/statistics`);
    return response.data;
  }

  async getCommentStatisticsByUser(userId: string): Promise<any> {
    const response = await apiClient.get<any>(`${this.baseUrl}/statistics/by-user/${userId}`);
    return response.data;
  }

  async getCommentStatisticsByDateRange(startDate: string, endDate: string): Promise<any> {
    const params = { startDate, endDate };
    const queryString = buildQueryParams(params);
    const response = await apiClient.get<any>(`${this.baseUrl}/statistics/by-date-range?${queryString}`);
    return response.data;
  }
}

export const commentService = new CommentService();
